import { IconType } from "react-icons";
import { TbHelpOctagonFilled, TbRobot } from "react-icons/tb";
import { MdLocalPolice } from "react-icons/md";
import { GiTrade } from "react-icons/gi";
import { RiP2pFill, RiSpeedLine } from "react-icons/ri";
import { FaUser } from "react-icons/fa";
import { IoSettingsSharp } from "react-icons/io5";
import {
  FAQIcon,
  NewsIcon,
  OrdersIcon,
  ProductIcon,
  SolutionsIcon,
  WhitepaperIcon,
  P2POrdersIcon,
  TransactionsIcon,
  HomeIcon,
  PaymentIcon,
  ShopsIcon,
  IxSendIcon,
  WalletIcon,
  DisputesIcon,
  AssignUserIcon,
  AssignCheckIcon,
  LeaderboardIcon,
} from "@shared/src/components/assets/icons";
import { PendingOrder } from "@shared/src/components/common/dialogs/PendingOrder";
import { NotificationSettingsIcon } from "@shared/src/components/assets/icons/NotificationSettingIcon";
import { LucideFastForward } from "lucide-react";
import { BsFastForwardBtn, BsFillFastForwardFill } from "react-icons/bs";

type CustomComponentProps = {
  onNavigate?: () => void;
  className?: string;
};

export type NavigationItem = {
  href: string;
  label: string;
  icon: IconType;
  type: "link" | "dropdown" | "external";
  category?: "main" | "other" | "account" | "order-history" | "admin";
  children?: {
    href: string;
    label: string;
    isExternal?: boolean;
  }[];
  customContent?: React.ComponentType<CustomComponentProps>;
  onCloseParentDropdown?: () => void;
};

type MenuNavigationItem = {
  href: string;
  label: string;
  icon: IconType;
  onClick?: () => void;
};

// Help dropdown items
export const helpDropdownItems = [
  {
    href: "https://github.com/iexchange/docs/how-to-sell-buy.md",
    label: "How to sell/buy",
    isExternal: true,
  },
  {
    href: "https://github.com/iexchange/docs/placing-an-ad.md",
    label: "Placing an ad",
    isExternal: true,
  },
  {
    href: "https://github.com/iexchange/docs/how-to-be-a-merchant.md",
    label: "How to be a merchant",
    isExternal: true,
  },
  {
    href: "https://github.com/iexchange/docs/how-to-be-a-settler.md",
    label: "How to be a settler",
    isExternal: true,
  },
  {
    href: "/contracts",
    label: "Supported Networks",
    isExternal: false,
  },
];

// Main navigation items
const mainNavigation: NavigationItem[] = [
  {
    href: "/quick-trade",
    label: "Quick Trade",
    icon: BsFillFastForwardFill,
    type: "link",
    category: "main",
  },
  {
    href: "/",
    label: "P2P",
    icon: RiP2pFill,
    type: "link",
    category: "main",
  },
  {
    href: "/ixpay",
    label: "iXPay",
    icon: MdLocalPolice,
    type: "link",
    category: "main",
  },
  {
    href: "/help",
    label: "Help",
    icon: TbHelpOctagonFilled,
    type: "dropdown",
    category: "main",
    children: helpDropdownItems,
  },
  {
    href: "/p2p-orders",
    label: "Orders",
    icon: OrdersIcon as unknown as IconType,
    type: "dropdown",
    category: "main",
    customContent: PendingOrder,
  },
];

// Other navigation options
const otherNavigation: NavigationItem[] = [
  {
    href: "https://iexchange-landing-page.web.app#product",
    label: "Product",
    icon: ProductIcon,
    type: "external",
    category: "other",
  },
  {
    href: "https://iexchange-landing-page.web.app#solution",
    label: "Solutions",
    icon: SolutionsIcon,
    type: "external",
    category: "other",
  },
  {
    href: "https://iexchange-landing-page.web.app#whitepaper",
    label: "Whitepaper",
    icon: WhitepaperIcon,
    type: "external",
    category: "other",
  },
  {
    href: "https://iexchange-landing-page.web.app#faqs",
    label: "FAQs",
    icon: FAQIcon,
    type: "external",
    category: "other",
  },
  // {
  //   href: "https://iexchange-landing-page.web.app#news",
  //   label: "News",
  //   icon: NewsIcon,
  //   type: "external",
  //   category: "other",
  // },
];

// Account navigation
const accountNavigation: NavigationItem[] = [
  // {
  //   href: "/payment-methods",
  //   label: "Payment Methods",
  //   icon: PaymentIcon as unknown as IconType,
  //   type: "link",
  //   category: "account",
  // },
  {
    href: "/wallet",
    label: "Wallet",
    icon: WalletIcon as unknown as IconType,
    type: "link",
    category: "account",
  },
  {
    href: "/settings#notifications",
    label: "Notification",
    icon: NotificationSettingsIcon,
    type: "link",
    category: "main",
  },
];

// Admin main links navigation
export const adminMainLinks = [
  {
    href: "/analytics",
    label: "Analytics",
    icon: LeaderboardIcon,
    type: "link",
    category: "admin",
  },
  {
    href: "/kyc",
    label: "KYC",
    icon: AssignUserIcon,
    type: "link",
    category: "admin",
  },
  {
    href: "/autotrader",
    label: "Auto-Trader",
    icon: TbRobot,
    type: "link",
    category: "admin",
  },
  {
    href: "/appeals",
    label: "Appeals",
    icon: DisputesIcon,
    type: "dropdown",
    category: "admin",
    children: [
      { href: "/appeals", label: "Opened" },
      { href: "/appeals/assigned", label: "Assigned" },
      { href: "/appeals/judged", label: "Judged" },
      { href: "/appeals/completed", label: "Completed" },
    ],
  },
  {
    href: "/sanctions",
    label: "Sanctions Check",
    icon: AssignCheckIcon,
    type: "link",
    category: "admin",
  },
] as const;

// Main links navigation
export const mainLinks = [
  {
    href: "/dashboard",
    label: "Dashboard",
    icon: HomeIcon,
    type: "link",
    category: "main",
  },
  {
    href: "/my-shop",
    label: "My Shop",
    icon: ShopsIcon,
    type: "link",
    category: "main",
  },
  {
    href: "/auto-trading",
    label: "Auto Trading",
    icon: TbRobot as unknown as IconType,
    type: "link",
    category: "main",
  },
  {
    href: "/ixsend",
    label: "ixSend",
    icon: IxSendIcon,
    type: "link",
    category: "main",
  },
  {
    href: "/leaderboard",
    label: "Leaderboard",
    icon: LeaderboardIcon,
    type: "link",
    category: "main",
  },
] as const;

export const orderHistoryNavigation = [
  {
    href: "/p2p-orders",
    label: "P2P Orders",
    icon: P2POrdersIcon,
    type: "link",
    category: "order-history",
  },
  {
    href: "/payout-history",
    label: "Payout History",
    icon: TransactionsIcon,
    type: "link",
    category: "order-history",
  },
  {
    href: "/disputes",
    label: "Disputes",
    icon: DisputesIcon,
    type: "link",
    category: "order-history",
  },
] as const;

// Menu navigation items
export const menuNavigation: MenuNavigationItem[] = [
  {
    href: "/dashboard",
    label: "Dashboard",
    icon: HomeIcon,
  },
  {
    href: "/p2p-orders",
    label: "Order History",
    icon: OrdersIcon,
  },
  {
    href: "/settings",
    label: "Settings",
    icon: IoSettingsSharp,
  },
];

// Additional navigation items
export const additionalNavItems = [
  {
    href: "/help",
    label: "Help",
    icon: TbHelpOctagonFilled,
    type: "dropdown",
    children: helpDropdownItems,
  },
  {
    href: "/shops",
    label: "Shops",
    icon: ShopsIcon as unknown as IconType,
    type: "link",
  },
  {
    href: "/ixsend",
    label: "ixSend",
    icon: IxSendIcon as unknown as IconType,
    type: "link",
  },
  {
    href: "/ixpay",
    label: "iXPay",
    icon: MdLocalPolice as unknown as IconType,
    type: "link",
  },
  {
    href: "/leaderboard",
    label: "Leaderboard",
    icon: LeaderboardIcon as unknown as IconType,
    type: "link",
  },
  // {
  //   href: "/settings#notifications",
  //   label: "Notification",
  //   icon: NotificationSettingsIcon,
  //   type: "link",
  //   category: "main",
  // },
] as const;

export const navigationConfig = [...mainNavigation];
export const drawerNavigation = {
  main: mainNavigation,
  other: otherNavigation,
  account: accountNavigation,
};
